'use client';

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSetState, useEffectOnce, useUpdateEffect } from 'react-use';

import { produce, type WritableDraft } from 'immer';
import { type DebouncedFuncLeading, debounce, throttle, type DebouncedFunc } from 'lodash';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';

import type { VideoFeed } from '@/app/actions/movie/types';
import { useRouter, usePathname } from '@/i18n/navigation';
import type { Locale } from '@/i18n/routing';
import type { ChildrenProps } from '@/types/common.types';

import { useVerification } from '@/contexts/VerificationContext/useVerification';

import { MovieApi } from '@/api/domains/movie.api';
import { ErrorResponse } from '@/api/types/common.types';
import type { MovieFile, SerialFiles } from '@/api/types/movie.types';

import { customLocalStorage } from '@/helpers/storage.helpers';

import { httpClient } from '@/libs/http-client';

import {
  HTTP_CODE_PAYMENT_REQUIRED,
  HTTP_CODE_SUCCESS,
  HTTP_CODE_UNAUTHORIZED,
} from '@/constants/http-code.constants';
import { USER_ACCESS_TOKEN } from '@/constants/user-credentials.constants';

import { useNetwork } from '@/contexts/NetworkContext';
import { useNotification } from '@/contexts/NotificationContext';
import { MovieQueryKeys } from '@/contexts/QueryContext/query.keys';

import { PlayerModal } from '@/views/MovieDetailsView/MoviePlayerContext/PlayerModal';

// =================================================================

const LIMIT_OF_FILES_PER_PAGE = 10;

const initialModalState = {
  isShow: false,
  positionX: 0,
  positionY: 0,
};

// =================================================================

export type LastPositionProps = {
  playerFeedStatus: PlayerFeedStatus;
  locale?: string;
};

type ExtraPlayerContextState = {
  paused: boolean;
  currentTime: string | null;
  modalState: { isShow: boolean; positionX: number; positionY: number };
  isAuthenticated: boolean;
  isTasix: boolean;
  locale: Locale;
};

type PlayerContextState = {
  isFetchingFeed: boolean;
  moduleId: string;
  movieId: string;
  persistedFeedFileId: string | null;
  currentFeedFileId: string | null;
  currentSeasonId: string | null;
  currentPage: number;
  feed: VideoFeed | null;
  feedSeasonId: string | null;
  feedPage: string | null;
  feedLastPosition: string | null;
  previousFeedFileId: string | null;
  nextFeedFileId: string | null;
  shouldRenderPlayButton: boolean;
  selectedMovieFile: { id: number; title: string } | null;
  movieTitle: string;
  serialEpisodeTitle: string;
  hasMiniPlayer: boolean;
};

type PlayerContextMethods = {
  setCurrentFeedFileId: (fileId: number) => void;
  setCurrentSeasonId: (seasonId: number) => void;
  setCurrentPage: (page: number) => void;
  setShouldRenderPlayButton: (value: boolean) => void;
  persistLastPosition:
    | DebouncedFuncLeading<
        (fileId: number | string, playerFeedStatus: PlayerFeedStatus, locale?: string) => void
      >
    | undefined;
  fetchFeed: (feedFileId: number) => Promise<void>;
  fetchFeedTrailer: (feedFileId: number) => Promise<void>;
  setPaused: Dispatch<SetStateAction<boolean>>;
  setCurrentModalState: (value?: boolean, x?: number, y?: number) => void;
  updateProgressBar?: DebouncedFunc<(props: LastPositionProps) => void>;
  handleOpenPlayerModal: () => void;
  handleClosePlayerModal: () => void;
  handleSelectMovieFile: (params: { id: number; title: string }) => void;
  clearMovieFiles: () => void;
  setMovieTitle: (title: string) => void;
  setSerialEpisodeTitle: (title: string) => void;
  setHasMiniPlayer: (value: boolean) => void;
};

export type PlayerFeedStatus = {
  currentTime: number;
  duration: number;
};

// =================================================================

export const PlayerContext = createContext<
  (PlayerContextState & ExtraPlayerContextState & PlayerContextMethods) | null
>(null);

// =================================================================

export const PlayerContextProvider = (props: ChildrenProps & { movieTitle: string }) => {
  const { children, movieTitle } = props;

  const t = useTranslations('page.movieDetail');
  const queryClient = useQueryClient();

  const locale = useLocale() as Locale;

  const { notification } = useNotification();

  const {
    userAccessToken,
    checkAuthVerification,
    checkSubscriptionVerification,
    closeVerificationModal,
    setSubscriptionTitle,
    setUserAccessToken,
    setUserCredentials,
    authVerify,
  } = useVerification();

  const { isTasix } = useNetwork();

  const router = useRouter();
  const pathname = usePathname();

  const params = useParams();
  const { moduleId: persistedModuleId, movieId: persistedMovieId } = params as {
    moduleId: string;
    movieId: string;
  };

  const searchParams = useSearchParams();
  const queryPersistedFeedFileId = searchParams.get('fileId');
  const queryPersistedSeasonId = searchParams.get('seasonId');
  const queryPersistedPage = searchParams.get('page');
  const queryPersistedLastPosition = searchParams.get('lastPosition');
  const autoplay = searchParams.get('autoplay');
  const isUrlFromContext = searchParams.get('isUrlFromContext');

  const [mediaState, setMediaState] = useSetState<PlayerContextState>({
    isFetchingFeed: false,
    moduleId: persistedModuleId,
    movieId: persistedMovieId,
    persistedFeedFileId: queryPersistedFeedFileId,
    currentFeedFileId: null,
    currentSeasonId: queryPersistedSeasonId,
    currentPage: queryPersistedPage ? Number(queryPersistedPage) : 1,
    feed: null,
    feedSeasonId: null,
    feedPage: null,
    feedLastPosition: queryPersistedLastPosition,
    previousFeedFileId: null,
    nextFeedFileId: null,
    shouldRenderPlayButton: false,
    selectedMovieFile: null,
    movieTitle,
    serialEpisodeTitle: '',
    hasMiniPlayer: false,
  });

  const [modalState, setModalState] = useSetState(initialModalState);

  const currentTimeToCopyUrl = useRef(queryPersistedLastPosition);
  const [paused, setPaused] = useState(false);

  // ----------------------------------------------------------------

  // Player modal actions state
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);

  const handleClosePlayerModal = useCallback(() => {
    setPaused(false);
    setIsPlayerModalOpen(false);
  }, []);

  const handleOpenPlayerModal = useCallback(() => {
    if (userAccessToken) {
      setIsPlayerModalOpen(true);
    }
  }, [userAccessToken]);

  // ----------------------------------------------------------------

  const setCurrentModalState = (value = false, x = 0, y = 0) => {
    setModalState({ isShow: value, positionX: x, positionY: y });
  };

  useEffect(() => {
    const handleCloseModal = () => {
      if (modalState.isShow) {
        setCurrentModalState(false);
      }
    };

    document.addEventListener('click', handleCloseModal);

    return () => document.removeEventListener('click', handleCloseModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.isShow]);

  // ----------------------------------------------------------------

  const handleSelectMovieFile = (params: { id: number; title: string }) => {
    setMediaState({
      selectedMovieFile: params,
    });
  };

  // ----------------------------------------------------------------

  const clearMovieFiles = () => {
    setMediaState({
      selectedMovieFile: null,
    });
  };

  // ----------------------------------------------------------------

  const setCurrentFeedFileId = (fileId: number) =>
    setMediaState({ currentFeedFileId: fileId.toString() });

  // ----------------------------------------------------------------

  const setCurrentSeasonId = (seasonId: number) => {
    const currentSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
    currentSearchParams.delete('seasonId');
    currentSearchParams.set('seasonId', `${seasonId}`);
    currentSearchParams.delete('page');
    currentSearchParams.set('page', '1');
    const query = currentSearchParams.toString();

    setMediaState({ currentSeasonId: seasonId.toString(), currentPage: 1 });
    router.replace(`${pathname}?${query}`, { scroll: false });
  };

  // ----------------------------------------------------------------

  const setShouldRenderPlayButton = (value: boolean) =>
    setMediaState({ shouldRenderPlayButton: value });

  // ----------------------------------------------------------------

  const setHasMiniPlayer = (value: boolean) => {
    setMediaState({ hasMiniPlayer: value });
  };

  // ----------------------------------------------------------------

  const setCurrentPage = (page: number) => setMediaState({ currentPage: page });

  // ----------------------------------------------------------------

  const setMovieTitle = (title: string) => {
    setMediaState({ movieTitle: title });
  };

  // ----------------------------------------------------------------

  const setSerialEpisodeTitle = (title: string) => {
    setMediaState({ serialEpisodeTitle: title });
  };

  // ----------------------------------------------------------------

  const fetchFeed = checkAuthVerification<number>({
    onSuccess: async feedFileId => {
      const persistedToken = userAccessToken || customLocalStorage.getItem(USER_ACCESS_TOKEN);
      setMediaState({ isFetchingFeed: Boolean(feedFileId), feed: null });

      if (!mediaState.hasMiniPlayer && !isPlayerModalOpen) {
        handleOpenPlayerModal();
      }

      try {
        const response = await MovieApi.fetchVideoFeed(feedFileId!, persistedToken as string);
        if (response.code === HTTP_CODE_SUCCESS) {
          const feed = response.data;

          const isPlayingSerial = Boolean(feed.season_id);
          const { moduleId, movieId } = mediaState;

          let feedLastPosition: string | undefined;
          let additionalStateParams:
            | {
                currentSeasonId: string;
                currentPage: number;
                feedSeasonId: string;
                feedPage: string;
                nextFeedFileId: string | undefined;
                previousFeedFileId: string | undefined;
              }
            | undefined;
          let additionalQueryParams: { seasonId: string; page: string } | undefined;

          if (isPlayingSerial) {
            const { season_id } = feed;
            const idsOfAllFilesInSeason = await MovieApi.fetchFilesIdsBySeasonId({
              moduleId,
              movieId,
              seasonId: season_id!.toString(),
            });
            const indexOfCurrentFeedOnPage = idsOfAllFilesInSeason.indexOf(feedFileId!);
            const orderOfCurrentFeedOnPage = indexOfCurrentFeedOnPage + 1;

            const feedPage = Math.ceil(orderOfCurrentFeedOnPage / LIMIT_OF_FILES_PER_PAGE);

            const hasPrevFeed = Boolean(idsOfAllFilesInSeason[indexOfCurrentFeedOnPage - 1]);
            let previousFeedFileId: string | undefined;
            if (hasPrevFeed) {
              previousFeedFileId = idsOfAllFilesInSeason[indexOfCurrentFeedOnPage - 1].toString();
            }

            const hasNextFeed =
              feedFileId !== idsOfAllFilesInSeason[idsOfAllFilesInSeason.length - 1];
            let nextFeedFileId: string | undefined;
            if (hasNextFeed) {
              nextFeedFileId = idsOfAllFilesInSeason[indexOfCurrentFeedOnPage + 1].toString();
            }

            if (queryPersistedLastPosition) {
              feedLastPosition = queryPersistedLastPosition;
            } else {
              let serialFiles: { files: MovieFile[]; lastPage: number };
              const isEpisodesPage = pathname.endsWith('/episodes');

              const serialCacheKey = isEpisodesPage
                ? MovieQueryKeys.serialFiles({
                    moduleId,
                    movieId,
                    seasonId: season_id!.toString(),
                    page: feedPage,
                    locale,
                  })
                : MovieQueryKeys.serialInfiniteFiles({
                    moduleId,
                    movieId,
                    seasonId: season_id!.toString(),
                    locale,
                  });
              const cachedSerialFiles = queryClient.getQueryData(serialCacheKey);
              if (cachedSerialFiles) {
                if (isEpisodesPage) {
                  serialFiles = cachedSerialFiles as { files: MovieFile[]; lastPage: number };
                } else {
                  const { pages } = cachedSerialFiles as { pages: SerialFiles[] };
                  serialFiles = {
                    files: pages[0].files,
                    lastPage: pages[0].lastPage,
                  };
                }
              } else {
                if (isEpisodesPage) {
                  serialFiles = await queryClient.fetchQuery({
                    queryKey: MovieQueryKeys.serialFiles({
                      moduleId,
                      movieId,
                      seasonId: season_id!.toString(),
                      page: feedPage,
                      locale,
                    }),
                    queryFn: () =>
                      MovieApi.fetchSerialFiles({
                        moduleId,
                        movieId,
                        seasonId: season_id!.toString(),
                        page: feedPage,
                        locale,
                      }),
                  });
                } else {
                  const { pages } = await queryClient.fetchInfiniteQuery({
                    queryKey: MovieQueryKeys.serialInfiniteFiles({
                      moduleId,
                      movieId,
                      seasonId: season_id!.toString(),
                      locale,
                    }),
                    queryFn: ({ pageParam = 1 }) =>
                      MovieApi.fetchSerialFiles({
                        moduleId,
                        movieId,
                        seasonId: season_id!.toString(),
                        page: pageParam,
                        locale,
                      }),
                    initialPageParam: 1,
                  });

                  serialFiles = {
                    files: pages[0].files,
                    lastPage: pages[0].lastPage,
                  };
                }
              }

              const selectedSerialFile = serialFiles.files.find(serial => serial.id === feedFileId);

              feedLastPosition = selectedSerialFile?.isWatched
                ? '0'
                : selectedSerialFile?.lastPosition.toString();

              additionalStateParams = {
                currentSeasonId: season_id!.toString(),
                currentPage: feedPage,
                feedSeasonId: season_id!.toString(),
                feedPage: feedPage.toString(),
                nextFeedFileId,
                previousFeedFileId,
              };
              additionalQueryParams = {
                seasonId: season_id!.toString(),
                page: feedPage.toString(),
              };
            }
          } else {
            if (queryPersistedLastPosition) {
              feedLastPosition = queryPersistedLastPosition;
            } else {
              const movieCacheKey = MovieQueryKeys.movieFiles(moduleId, movieId);
              const movieFiles = queryClient.getQueryData(movieCacheKey) as MovieFile[];
              feedLastPosition = movieFiles
                .find(file => file.id === feedFileId)!
                .lastPosition.toString();
            }
          }

          setMediaState(state => ({
            isFetchingFeed: false,
            feed,
            previousFeedFileId: state.feed?.id.toString(),
            feedLastPosition,
            currentFeedFileId: `${feedFileId}`,
            ...additionalStateParams,
          }));

          // Get the current URL and query parameters
          const currentUrl = new URL(window.location.href);
          const currentSearchParams = new URLSearchParams(currentUrl.search);
          currentSearchParams.delete('autoplay');
          currentSearchParams.delete('lastPosition');
          currentSearchParams.delete('isUrlFromContext');
          currentSearchParams.delete('fileId');
          currentSearchParams.set('fileId', `${feedFileId}`);
          if (additionalQueryParams?.seasonId) {
            currentSearchParams.delete('seasonId');
            currentSearchParams.set('seasonId', `${additionalQueryParams.seasonId}`);
          }
          if (additionalQueryParams?.page) {
            currentSearchParams.delete('page');
            currentSearchParams.set('page', `${additionalQueryParams.page}`);
          }

          const query = currentSearchParams.toString();
          closeVerificationModal();
          router.replace(`${pathname}?${query}`, { scroll: false });
        }
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        setIsPlayerModalOpen(false); // close player modal when required payment

        const { message } = error.response;
        notification('error', message);

        if (error.response.code === HTTP_CODE_PAYMENT_REQUIRED) {
          const { moduleId } = mediaState;

          checkSubscriptionVerification({
            onSuccess: () => {
              closeVerificationModal();
              router.replace(`${pathname}?autoplay=true`);
            },
            onMountFn: () => {
              setSubscriptionTitle(
                t('subscriptionTitle', {
                  content: movieTitle,
                }),
              );
              router.replace(`${pathname}?moduleId=${moduleId}`);
            },
          });
          setMediaState({ isFetchingFeed: false });
        }
      }
    },
  }) as (feedFileId: number) => Promise<void>;

  // ----------------------------------------------------------------

  const fetchFeedTrailer = checkAuthVerification<number>({
    onSuccess: async feedFileId => {
      setMediaState({ isFetchingFeed: Boolean(feedFileId), feed: null });

      handleOpenPlayerModal();

      try {
        const response = await MovieApi.fetchTrailerVideoFeed(feedFileId!);
        if (response.code === HTTP_CODE_SUCCESS) {
          const feed = response.data;

          setMediaState(state => ({
            isFetchingFeed: false,
            feed,
          }));

          // // Get the current URL and query parameters
          // const currentUrl = new URL(window.location.href);
          // const currentSearchParams = new URLSearchParams(currentUrl.search);
          // currentSearchParams.delete('autoplay');
          // currentSearchParams.delete('lastPosition');
          // currentSearchParams.delete('isUrlFromContext');
          // currentSearchParams.delete('fileId');
          // currentSearchParams.set('fileId', `${feedFileId}`);

          // const query = currentSearchParams.toString();
          closeVerificationModal();
          // router.replace(`${pathname}?${query}`, { scroll: false });
        }
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        setIsPlayerModalOpen(false); // close player modal when required payment

        const { message } = error.response;
        notification('error', message);
      }
    },
  }) as (feedFileId: number) => Promise<void>;

  // ----------------------------------------------------------------

  const updateProgressBar = useMemo(() => {
    if (!mediaState.feed) {
      return;
    }

    const { moduleId, movieId, currentFeedFileId, feedSeasonId, feedPage } = mediaState;

    const isPlayingSerial = Boolean(feedSeasonId);
    const isEpisodesPage = pathname.endsWith('/episodes');

    const cacheKey = !isPlayingSerial
      ? MovieQueryKeys.movieFiles(moduleId, movieId)
      : isEpisodesPage
        ? MovieQueryKeys.serialFiles({
            moduleId,
            movieId,
            seasonId: feedSeasonId!,
            page: feedPage ? Number(feedPage) : 1,
            locale,
          })
        : MovieQueryKeys.serialInfiniteFiles({
            moduleId,
            movieId,
            seasonId: feedSeasonId!,
            locale,
          });

    const updateFeedProgress = ({ playerFeedStatus }: LastPositionProps) => {
      const { currentTime, duration } = playerFeedStatus;

      queryClient.setQueryData(
        cacheKey,
        (old: { pageParams: number[]; pages: SerialFiles[] } | SerialFiles | MovieFile[]) => {
          // Update movie files
          if (!isPlayingSerial) {
            return produce(old, draft => {
              const fileToUpdate = (draft as WritableDraft<MovieFile[]>).find(
                file => file.id === Number(currentFeedFileId),
              );

              if (fileToUpdate) {
                fileToUpdate.lastPosition = currentTime;
                fileToUpdate.isWatched = currentTime >= duration * 0.98;
              }
            });
          }

          // Update infinite serial files
          if (!isEpisodesPage && isPlayingSerial) {
            return produce(old, draft => {
              const fileToUpdate = (
                draft as WritableDraft<{ pageParams: number[]; pages: SerialFiles[] }>
              ).pages[0].files.find(file => file.id === Number(currentFeedFileId));

              if (fileToUpdate) {
                fileToUpdate.lastPosition = currentTime;
                fileToUpdate.isWatched = currentTime >= duration * 0.98;
              }
            });
          }

          // Update episodes page serial files
          return produce(old, draft => {
            const fileToUpdate = (draft as WritableDraft<SerialFiles>).files.find(
              file => file.id === Number(currentFeedFileId),
            );

            if (fileToUpdate) {
              fileToUpdate.lastPosition = currentTime;
              fileToUpdate.isWatched = currentTime >= duration * 0.98;
            }
          });
        },
      );
    };

    return throttle(updateFeedProgress, 1000, { leading: false });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaState.feed]);

  // ----------------------------------------------------------------

  const persistLastPosition = useMemo(() => {
    if (!mediaState.feed || !userAccessToken) {
      return;
    }

    const persistFeedLastPosition = async (
      fileId: number | string,
      playerFeedStatus: PlayerFeedStatus,
      locale?: string,
    ) => {
      try {
        const { currentTime, duration } = playerFeedStatus;
        const isNearTheEnd = currentTime >= duration * 0.98;
        await MovieApi.saveLastPosition({
          file_id: Number(fileId),
          movie_id: Number(persistedMovieId),
          is_watched: isNearTheEnd,
          last_position: isNearTheEnd ? 0 : currentTime,
          locale,
        });
      } catch (error) {
        const { code } = error.response as ErrorResponse;
        if (code === HTTP_CODE_UNAUTHORIZED) {
          delete httpClient.defaults.headers['Cinerama-Token'];
          customLocalStorage.removeItem(USER_ACCESS_TOKEN);
          setUserAccessToken(null);
          setUserCredentials(null);
          authVerify();
        }
      }
    };

    return debounce(persistFeedLastPosition, 1000, { leading: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaState.feed, userAccessToken]);

  // ----------------------------------------------------------------

  useEffect(() => {
    if (queryPersistedPage) {
      setCurrentPage(Number(queryPersistedPage));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPersistedPage]);

  // ----------------------------------------------------------------

  // update state when fileId , seasonId changes
  useEffect(() => {
    setMediaState({
      persistedFeedFileId: queryPersistedFeedFileId,
      currentSeasonId: queryPersistedSeasonId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPersistedFeedFileId, queryPersistedSeasonId]);

  // ----------------------------------------------------------------

  // by clicking on play and triggers autoplay
  useEffect(() => {
    if (autoplay) {
      const handleAutoplay = async () => {
        if (mediaState.currentFeedFileId) {
          await fetchFeed(Number(mediaState.currentFeedFileId));
        }
      };

      handleAutoplay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, mediaState.currentFeedFileId]);

  // Handles the case when user was redirected from
  // home page by clicking on an unfinished card.
  useEffectOnce(() => {
    if (!userAccessToken && isUrlFromContext && autoplay) {
      return;
    }

    if (mediaState.persistedFeedFileId && autoplay) {
      setMediaState({ currentFeedFileId: mediaState.persistedFeedFileId });
    }
  });

  // Handles the case when [movieId] is changed by clicking
  // on any related movies cards or cards in suggested search dropdown.
  // We need to sync [mediaState.movieId] with selected [movieId], and
  // reset all data for the current feed so that file cards get updated
  // and player disposes of the current feed.
  useUpdateEffect(() => {
    setMediaState({
      movieId: persistedMovieId,
      persistedFeedFileId: null,
      currentFeedFileId: null,
      currentSeasonId: null,
      currentPage: 1,
      feed: null,
      feedSeasonId: null,
      feedPage: null,
      feedLastPosition: null,
      previousFeedFileId: null,
      nextFeedFileId: null,
    });
  }, [persistedMovieId]);

  // ----------------------------------------------------------------

  // Handle movie title when its quality is changed
  useEffect(() => {
    const movieFile = mediaState.selectedMovieFile;
    if (movieFile) {
      setMovieTitle(`${movieTitle} (${movieFile.title})`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaState.selectedMovieFile]);

  const isSerial = Boolean(mediaState.feed?.season_id);

  return (
    <PlayerContext.Provider
      value={{
        ...mediaState,
        paused,
        modalState,
        currentTime: currentTimeToCopyUrl.current,
        isAuthenticated: Boolean(userAccessToken),
        isTasix: Boolean(isTasix),
        locale,

        // methods
        setCurrentFeedFileId,
        setCurrentSeasonId,
        setCurrentPage,
        setShouldRenderPlayButton,
        persistLastPosition,
        fetchFeed,
        fetchFeedTrailer,
        setPaused,
        setCurrentModalState,
        updateProgressBar,
        handleOpenPlayerModal,
        handleClosePlayerModal,
        handleSelectMovieFile,
        clearMovieFiles,
        setMovieTitle,
        setSerialEpisodeTitle,
        setHasMiniPlayer,
      }}
    >
      {children}
      <PlayerModal
        title={movieTitle}
        isSerial={isSerial}
        isFetchingFeed={mediaState.isFetchingFeed}
        shouldUseAnimation={false}
        isOpen={isPlayerModalOpen && !mediaState.hasMiniPlayer}
        size="xl"
        onClose={handleClosePlayerModal}
        canOutsideClickClose={false}
        shouldFocusOnMount
      />
    </PlayerContext.Provider>
  );
};
